import { Plus, Trash2, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { PriceListPicker } from "./PriceListPicker";
import { api, errMsg } from "../../lib/api";
import { lineTotal, money, uid } from "../../lib/format";

const saveToPriceList = async (i) => {
  if (!i.description.trim()) return toast.error("Add a description first");
  try {
    await api.post("/price-items", { name: i.description, pricing_type: i.pricing_type, unit_price: Number(i.unit_price) || 0 });
    toast.success("Saved to price list");
  } catch (e) { toast.error(errMsg(e)); }
};

const PRICING = { each: "Each", length: "Per metre", area: "Per m²" };
const cell = "h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";

const Num = ({ value, onChange, testId, disabled }) => (
  <input data-testid={testId} type="number" step="any" min={0} disabled={disabled} value={disabled ? "" : value}
    placeholder={disabled ? "—" : ""} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
    className={`${cell} text-right font-mono disabled:bg-zinc-50`} />
);

export const LineItems = ({ items, onChange }) => {
  const set = (id, k, v) => onChange(items.map((i) => (i.id === id ? { ...i, [k]: v } : i)));
  const add = () => onChange([...items, { id: uid(), description: "", pricing_type: "each", quantity: 1, length: 0, width: 0, unit_price: 0 }]);
  return (
    <div data-testid="line-items" className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
      <table className="w-full min-w-[900px] text-sm">
        <thead className="bg-zinc-950 text-left font-mono text-[11px] uppercase tracking-wider text-zinc-300">
          <tr>
            <th className="px-3 py-2.5">Description</th><th className="w-32 px-2">Pricing</th><th className="w-20 px-2 text-right">Qty</th>
            <th className="w-24 px-2 text-right">Length m</th><th className="w-24 px-2 text-right">Width m</th>
            <th className="w-28 px-2 text-right">Rate $</th><th className="w-28 px-2 text-right">Amount</th><th className="w-16" />
          </tr>
        </thead>
        <tbody>
          {items.map((i, idx) => (
            <tr key={i.id} data-testid={`line-item-row-${idx}`} className="border-t border-zinc-100">
              <td className="px-3 py-2"><input data-testid={`line-item-description-${idx}`} className={cell} value={i.description} placeholder="e.g. Supply & install 50mm copper water line" onChange={(e) => set(i.id, "description", e.target.value)} /></td>
              <td className="px-2"><select data-testid={`line-item-pricing-${idx}`} className={cell} value={i.pricing_type} onChange={(e) => set(i.id, "pricing_type", e.target.value)}>{Object.entries(PRICING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td>
              <td className="px-2"><Num testId={`line-item-qty-${idx}`} value={i.quantity} onChange={(v) => set(i.id, "quantity", v)} /></td>
              <td className="px-2"><Num testId={`line-item-length-${idx}`} disabled={i.pricing_type === "each"} value={i.length} onChange={(v) => set(i.id, "length", v)} /></td>
              <td className="px-2"><Num testId={`line-item-width-${idx}`} disabled={i.pricing_type !== "area"} value={i.width} onChange={(v) => set(i.id, "width", v)} /></td>
              <td className="px-2"><Num testId={`line-item-rate-${idx}`} value={i.unit_price} onChange={(v) => set(i.id, "unit_price", v)} /></td>
              <td data-testid={`line-item-total-${idx}`} className="px-2 text-right font-mono font-semibold">{money(lineTotal(i))}</td>
              <td className="whitespace-nowrap px-2"><button data-testid={`line-item-save-price-${idx}`} title="Save to price list" onClick={() => saveToPriceList(i)} className="rounded p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900"><BookmarkPlus className="h-4 w-4" /></button><button data-testid={`line-item-delete-${idx}`} onClick={() => onChange(items.filter((x) => x.id !== i.id))} className="rounded p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
            </tr>
          ))}
          {!items.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-400">No line items yet.</td></tr>}
        </tbody>
      </table>
      <div className="flex gap-2 border-t border-zinc-100 p-3">
        <Button data-testid="add-line-item-button" variant="outline" size="sm" onClick={add}><Plus className="mr-1 h-4 w-4" />Add line item</Button>
        <PriceListPicker onPick={(li) => onChange([...items, li])} />
      </div>
    </div>
  );
};
